import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AutomationHardwareItem, AutomationFootprint, AutomationFootprintVersion, AutomationFootprintVersionStatus, AutomationVendorHardwareData, ClientDevice, ClientDeviceLog, ClientDeviceType, ClusterDashboardStatusGlobalVariable, Collection, DataserverHeartbeat, DockerContainerStatsGlobalVariable, EngineeringProject, EngineeringProjectEngineer, EngineeringUpdateMeeting, EngineeringUpdateMeetingUserContent, FactoryNode, FactoryNodeConfig, GlobalVariableDocumentIds, GmailUser, GmailUserDevice, GmailUserLogin, KnowledgeDocument, LabelPrinter, RobotProgram, RobotProgramGroup, RobotProgramStatus, Schematic, SchematicVersion, SchematicVersionStatus, SoftwareTask, Tag, UserTimeSession, LabelPrinterGroup, filterLabelPrintersByRoute } from '@prism/prism-shared';
import { Query } from 'appwrite';
import { Observable, defaultIfEmpty, forkJoin, from, map, of, shareReplay, switchMap } from 'rxjs';
import { AppwriteDatabaseService, fromAppwriteDoc, fromAppwriteDocs } from '@prism/angular-shared';

@Injectable({ providedIn: 'root' })
export class AutomationApiService {
    constructor(
        private appwrite: AppwriteDatabaseService,
        private http: HttpClient,
    ) { }

    public loadTags(): Observable<Tag[]> {
        return this.appwrite.collection<Tag>(Collection.tag).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        )
    }

    public setFactoryNode(factoryNode: FactoryNode): Observable<void> {
        return from(this.appwrite.collection(Collection.factoryNode).doc(factoryNode.id).set(factoryNode));
    }

    public loadFactoryNodes(): Observable<FactoryNode[]> {
        return this.appwrite.collection<FactoryNode>(Collection.factoryNode).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public loadFactoryNodeById(factoryNodeId: string): Observable<FactoryNode> {
        return this.appwrite.collection<FactoryNode>(Collection.factoryNode).doc(factoryNodeId).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public setFactoryNodeConfig(factoryNodeConfig: FactoryNodeConfig): Observable<void> {
        return from(this.appwrite.collection(Collection.factoryNodeConfig).doc(factoryNodeConfig.id).set(factoryNodeConfig));
    }

    public loadFactoryNodeConfigById(factoryNodeConfigId: string): Observable<FactoryNodeConfig> {
        return this.appwrite.collection<FactoryNodeConfig>(Collection.factoryNodeConfig).doc(factoryNodeConfigId).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public loadFactoryNodeConfigsByIds(factoryNodeConfigIds: string[]): Observable<FactoryNodeConfig[]> {
        return this.appwrite.listByIds<FactoryNodeConfig>(Collection.factoryNodeConfig, factoryNodeConfigIds);
    }

    public loadFactoryNodeConfigsByFactoryNodeId(factoryNodeId: string): Observable<FactoryNodeConfig[]> {
        return this.appwrite.collection<FactoryNodeConfig>(Collection.factoryNodeConfig, ref => ref.where('factoryNodeId', '==', factoryNodeId)).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public setRobotProgramGroup(programGroup: RobotProgramGroup): Observable<void> {
        return from(this.appwrite.collection(Collection.robotProgramGroup).doc(programGroup.id).set(programGroup));
    }

    public setRobotProgram(program: RobotProgram): Observable<void> {
        return from(this.appwrite.collection(Collection.robotProgram).doc(program.id).set(program));
    }

    public loadRobotProgramGroups(): Observable<RobotProgramGroup[]> {
        return this.appwrite.collection<RobotProgramGroup>(Collection.robotProgramGroup).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public loadRobotProgramGroupById(programGroupId: string): Observable<RobotProgramGroup> {
        return this.appwrite.collection<RobotProgramGroup>(Collection.robotProgramGroup).doc(programGroupId).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public loadRobotProgramsByRobotProgramGroupId(programGroupId: string): Observable<RobotProgram[]> {
        return this.appwrite.collection<RobotProgram>(Collection.robotProgram, ref => ref.where('programGroupId', '==', programGroupId)).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }

    public loadRobotProgramById(programId: string): Observable<RobotProgram> {
        return this.appwrite.collection<RobotProgram>(Collection.robotProgram).doc(programId).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public calculateNextRobotProgramVersionNumberByRobotProgramGroupId(programGroupId: string): Observable<number> {
        return this.appwrite.collection(Collection.robotProgram, ref => ref
            .where('status', '==', RobotProgramStatus.RELEASED)
            .where('programGroupId', '==', programGroupId)
        ).get().pipe(
            map((resp) => resp.docs.length + 1)
        );
    }

    public deleteRobotProgram(programId: string): Observable<void> {
        return from(this.appwrite.collection(Collection.robotProgram).doc(programId).delete());
    }

    public setClientDevice(clientDevice: ClientDevice): Observable<void> {
        return from(this.appwrite.collection<ClientDevice>(Collection.clientDevice).doc(clientDevice.id).set(clientDevice));
    }
    public setLabelPrinter(labelPrinter: LabelPrinter): Observable<void> {
        return from(this.appwrite.collection(Collection.labelPrinter).doc(labelPrinter.id).set(labelPrinter));
    }
    public loadClientDevices(): Observable<ClientDevice[]> {
        return this.appwrite.list<ClientDevice>(Collection.clientDevice, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }
    public loadClientDevicesByType(type: ClientDeviceType): Observable<ClientDevice[]> {
        return this.appwrite.list<ClientDevice>(Collection.clientDevice, [
            Query.equal('type', type),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }
    public loadLabelPrinters(): Observable<LabelPrinter[]> {
        return this.appwrite.collection<LabelPrinter>(Collection.labelPrinter).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }
    public loadLabelPrinterGroups(): Observable<LabelPrinterGroup[]> {
        return this.appwrite.collection<LabelPrinterGroup>(Collection.labelPrinterGroup).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        );
    }
    /** Loads all label printers and groups, filtered down to the given route (deepest matching group context wins; no match => all printers). */
    public loadLabelPrintersForRoute(routeUrl: string): Observable<LabelPrinter[]> {
        return forkJoin([this.loadLabelPrinters(), this.loadLabelPrinterGroups()]).pipe(
            map(([labelPrinters, labelPrinterGroups]) => filterLabelPrintersByRoute(labelPrinters, labelPrinterGroups, routeUrl)),
            shareReplay(1)
        );
    }
    /** Loads exactly the printers assigned to a named group; a missing group has no fallback. */
    public loadLabelPrintersForGroupStrict(groupName: string): Observable<LabelPrinter[]> {
        return forkJoin([this.loadLabelPrinters(), this.loadLabelPrinterGroups()]).pipe(
            map(([labelPrinters, labelPrinterGroups]) => {
                const normalizedName = groupName.trim().toLocaleLowerCase();
                const group = labelPrinterGroups.find((candidate) => (
                    candidate.name.trim().toLocaleLowerCase() === normalizedName
                ));
                if (!group) return [];
                const printerIds = new Set(group.labelPrinterIds);
                return labelPrinters.filter((printer) => printerIds.has(printer.id));
            }),
            shareReplay(1),
        );
    }
    public setLabelPrinterGroup(labelPrinterGroup: LabelPrinterGroup): Observable<void> {
        return from(this.appwrite.collection(Collection.labelPrinterGroup).doc(labelPrinterGroup.id).set(labelPrinterGroup));
    }
    public deleteLabelPrinterGroup(labelPrinterGroupId: string): Observable<void> {
        return from(this.appwrite.collection(Collection.labelPrinterGroup).doc(labelPrinterGroupId).delete());
    }
    public loadLabelPrinterById(labelPrinterId: string): Observable<LabelPrinter> {
        return this.appwrite.get<LabelPrinter>(Collection.labelPrinter, labelPrinterId);
    }
    public loadClientDeviceById(clientDeviceId: string): Observable<ClientDevice> {
        return this.appwrite.get<ClientDevice>(Collection.clientDevice, clientDeviceId);
    }

    public getClientDevicesValueChanges(): Observable<ClientDevice[]> {
        return this.appwrite.collection<ClientDevice>(
            Collection.clientDevice,
            ref => ref.limit(1000)
        ).valueChanges();
    }
    public getLabelPrintersValueChanges(): Observable<LabelPrinter[]> {
        return this.appwrite.collection<LabelPrinter>(Collection.labelPrinter).valueChanges();
    }

    public getFactoryNodesValueChanges(): Observable<FactoryNode[]> {
        return this.appwrite.collection<FactoryNode>(Collection.factoryNode).valueChanges();
    }

    public getDataserverHeartbeatValueChanges(
        dataserverDocId = GlobalVariableDocumentIds.OnsiteUtilitiesHeartbeat,
    ): Observable<DataserverHeartbeat> {
        return this.appwrite.collection<DataserverHeartbeat>(Collection.globalVariables).doc(dataserverDocId).valueChanges().pipe(
            map(d => d as DataserverHeartbeat),
        );
    }

    public loadDockerContainerStats(): Observable<DockerContainerStatsGlobalVariable> {
        return this.appwrite.collection<DockerContainerStatsGlobalVariable>(Collection.globalVariables).doc(GlobalVariableDocumentIds.DockerContainerStats).get().pipe(
            map(resp => fromAppwriteDoc(resp))
        )
    }

    public loadClusterDashboardStatus(): Observable<ClusterDashboardStatusGlobalVariable> {
        return this.appwrite.collection<ClusterDashboardStatusGlobalVariable>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ClusterDashboardStatus).get().pipe(
            map(resp => fromAppwriteDoc(resp))
        )
    }



    public getFactoryNodeConfigsValueChanges(): Observable<FactoryNodeConfig[]> {
        return this.appwrite.collection<FactoryNodeConfig>(Collection.factoryNodeConfig).valueChanges();
    }

    public updateClientDevice(deviceId: string, partial: Partial<ClientDevice>): Observable<void> {
        return from(this.appwrite.collection<ClientDevice>(Collection.clientDevice).doc(deviceId).update(partial));
    }

    public setClientDeviceLog(clientDeviceLog: ClientDeviceLog): Observable<void> {
        return from(this.appwrite.collection<ClientDeviceLog>(Collection.clientDeviceLog).doc(clientDeviceLog.id).set(clientDeviceLog));
    }

    public getClientDeviceLogsByClientDeviceIdValueChanges(clientDeviceId: string): Observable<ClientDeviceLog[]> {
        return this.appwrite.collection<ClientDeviceLog>(
            Collection.clientDeviceLog,
            ref => ref
                .where('clientDeviceId', '==', clientDeviceId)
                .limit(100)
        ).valueChanges();
    }

    public setRefreshClientDevices(deviceIds: string[]): Observable<void> {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.RefreshClientDevices).set({ deviceIds }));
    }

    public removeRefreshClientDevice(deviceId: string): Observable<void> {
        return this.appwrite.get<{ deviceIds: string[] }>(Collection.globalVariables, GlobalVariableDocumentIds.RefreshClientDevices).pipe(
            switchMap((refresh) => from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.RefreshClientDevices).set({ deviceIds: (refresh.deviceIds ?? []).filter(id => id !== deviceId) }))),
        );
    }

    public loadRefreshClientDevices(): Observable<string[]> {
        return this.appwrite.get<{ deviceIds: string[] }>(Collection.globalVariables, GlobalVariableDocumentIds.RefreshClientDevices).pipe(
            map(r => r.deviceIds ?? [])
        )
    }

    public getRefreshClientDevicesValueChanges(): Observable<string[]> {
        return this.appwrite.collection<{ deviceIds: string[] }>(Collection.globalVariables).doc(GlobalVariableDocumentIds.RefreshClientDevices).valueChanges().pipe(
            map((resp) => resp?.deviceIds || [])
        )
    }

    public setUserTimeSession(timeSession: UserTimeSession): Observable<void> {
        return from(this.appwrite.collection<UserTimeSession>(Collection.userTimeSession).doc(timeSession.id).set(timeSession))
    }

    public setUserTimeSessions(userTimeSessions: UserTimeSession[]): Observable<void[]> {
        return forkJoin(userTimeSessions.map((timeSession) => this.setUserTimeSession(timeSession))).pipe(
            defaultIfEmpty([])
        )
    }

    public loadUserTimeSessions(): Observable<UserTimeSession[]> {
        return from(this.appwrite.collection<UserTimeSession>(Collection.userTimeSession).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public deleteUserTimeSession(timeSessionId: string): Observable<void> {
        return from(this.appwrite.collection<UserTimeSession>(Collection.userTimeSession).doc(timeSessionId).delete())
    }

    public loadUserTimeSessionById(timeSessionId: string): Observable<UserTimeSession> {
        return from(this.appwrite.collection<UserTimeSession>(Collection.userTimeSession).doc(timeSessionId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ))
    }

    public loadUserTimeSessionsByIds(timeSessionIds: string[]): Observable<UserTimeSession[]> {
        return this.appwrite.listByIds<UserTimeSession>(Collection.userTimeSession, timeSessionIds);
    }

    public setSoftwareTask(softwareTask: SoftwareTask): Observable<void> {
        return from(this.appwrite.collection<SoftwareTask>(Collection.softwareTask).doc(softwareTask.id).set(softwareTask))
    }

    public loadSoftwareTasks(): Observable<SoftwareTask[]> {
        return from(this.appwrite.collection<SoftwareTask>(Collection.softwareTask).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public setEngineeringUpdateMeeting(meeting: EngineeringUpdateMeeting): Observable<void> {
        return from(this.appwrite.collection<EngineeringUpdateMeeting>(Collection.engineeringUpdateMeeting).doc(meeting.id).set(meeting))
    }

    public loadEngineeringUpdateMeetings(): Observable<EngineeringUpdateMeeting[]> {
        return from(this.appwrite.collection<EngineeringUpdateMeeting>(Collection.engineeringUpdateMeeting).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public loadEngineeringUpdateMeetingById(meetingId: string): Observable<EngineeringUpdateMeeting> {
        return from(this.appwrite.collection<EngineeringUpdateMeeting>(Collection.engineeringUpdateMeeting).doc(meetingId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ))
    }

    public setEngineeringUpdateMeetingUserContent(content: EngineeringUpdateMeetingUserContent): Observable<void> {
        return from(this.appwrite.collection<EngineeringUpdateMeetingUserContent>(Collection.engineeringUpdateMeetingUserContent).doc(content.id).set(content))
    }

    public setEngineeringUpdateMeetingUserContents(contents: EngineeringUpdateMeetingUserContent[]): Observable<void[]> {
        return forkJoin(contents.map(c => this.setEngineeringUpdateMeetingUserContent(c))).pipe(
            defaultIfEmpty([])
        )
    }

    public loadEngineeringUpdateMeetingUserContents(): Observable<EngineeringUpdateMeetingUserContent[]> {
        return from(this.appwrite.collection<EngineeringUpdateMeetingUserContent>(Collection.engineeringUpdateMeetingUserContent).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public loadEngineeringUpdateMeetingUserContentsByMeetingId(meetingId: string): Observable<EngineeringUpdateMeetingUserContent[]> {
        return from(this.appwrite.collection<EngineeringUpdateMeetingUserContent>(
            Collection.engineeringUpdateMeetingUserContent,
            ref => ref.where('meetingId', '==', meetingId)
        ).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public deleteEngineeringUpdateMeetingUserContent(content: EngineeringUpdateMeetingUserContent): Observable<void> {
        return from(this.appwrite.collection<EngineeringUpdateMeetingUserContent>(Collection.engineeringUpdateMeetingUserContent).doc(content.id).delete())
    }

    public loadKnowledgeDocuments(): Observable<KnowledgeDocument[]> {
        return from(this.appwrite.collection<KnowledgeDocument>(Collection.knowledgeDocument).get().pipe(
            map((resp) => fromAppwriteDocs(resp.docs))
        ))
    }

    public loadKnowledgeDocumentById(documentId: string): Observable<KnowledgeDocument> {
        return this.appwrite.collection<KnowledgeDocument>(Collection.knowledgeDocument).doc(documentId).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public setKnowledgeDocument(document: KnowledgeDocument): Observable<void> {
        return from(this.appwrite.collection<KnowledgeDocument>(Collection.knowledgeDocument).doc(document.id).set(document))
    }

    public deleteKnowledgeDocumentById(documentId: string): Observable<void> {
        return from(this.appwrite.collection<KnowledgeDocument>(Collection.knowledgeDocument).doc(documentId).delete())
    }

    public setSoftwareTasks(softwareTasks: SoftwareTask[]): Observable<void[]> {
        const updatedTasks$ = softwareTasks.map(t => this.setSoftwareTask(t))

        return forkJoin(updatedTasks$).pipe(
            defaultIfEmpty([])
        )
    }

    public getRecentGmailLoginAttemptsValueChanges(): Observable<GmailUserLogin[]> {
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return this.appwrite.collection<GmailUserLogin>(Collection.gmailUserLogin, ref => ref
            .where('time', '>=', oneWeekAgo)
            .where('onSite', '==', false)
            .orderBy('time', 'desc')
        ).valueChanges();
    }

    public loadGmailLoginAttempts(): Observable<GmailUserLogin[]> {
        return this.appwrite.collection<GmailUserLogin>(Collection.gmailUserLogin).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        )
    }

    public loadGmailUsers(): Observable<GmailUser[]> {
        return this.appwrite.collection<GmailUser>(Collection.gmailUser).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        )
    }

    public loadGmailUserDevices(): Observable<GmailUserDevice[]> {
        return this.appwrite.collection<GmailUserDevice>(Collection.gmailUserDevice).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        )
    }

    public loadRecentGmailUserDevices(): Observable<GmailUserDevice[]> {
        return this.appwrite.collection<GmailUserDevice>(Collection.gmailUserDevice, ref => ref
            .orderBy('createdOn', 'desc')
            .limit(8)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        )
    }

    // --- Schematics ---

    // Parent schematic records (id + schematicNumber); detail lives on the versions.
    public loadSchematicParents(): Observable<Schematic[]> {
        return this.appwrite.collection<Schematic>(Collection.schematic).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadSchematics(): Observable<SchematicVersion[]> {
        return this.appwrite.collection<SchematicVersion>(Collection.schematicVersion, ref =>
            ref.orderBy('updatedOn', 'desc')
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }
    public loadSchematicById(id: string): Observable<SchematicVersion> {
        return this.appwrite.collection<SchematicVersion>(Collection.schematicVersion).doc(id).get().pipe(
            map(fromAppwriteDoc)
        );
    }


    public setSchematic(schematic: SchematicVersion): Observable<void> {
        return from(this.appwrite.collection(Collection.schematicVersion).doc(schematic.id).set(schematic));
    }

    // The editor replaces its node/wire arrays on edits. Omit unchanged drawing arrays so
    // metadata edits and node-only/wire-only edits don't upload the entire schematic again.
    public saveSchematic(schematic: SchematicVersion, saved: SchematicVersion | null): Observable<void> {
        const { nodes, wires, ...metadata } = schematic;
        return this.appwrite.update<SchematicVersion>(Collection.schematicVersion, schematic.id, {
            ...metadata,
            ...(nodes !== saved?.nodes ? { nodes } : {}),
            ...(wires !== saved?.wires ? { wires } : {}),
        });
    }

    public deleteSchematic(id: string): Observable<void> {
        return from(this.appwrite.collection(Collection.schematicVersion).doc(id).delete());
    }

    public loadSchematicVersionsBySchematicId(schematicId: string): Observable<SchematicVersion[]> {
        return this.appwrite.collection<SchematicVersion>(Collection.schematicVersion, ref =>
            ref.where('schematicId', '==', schematicId)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    // Releases a schematic version: marks prior released/obsolete versions of the same
    // schematic Obsolete, assigns the next version number, sets this one Released, and
    // writes everything in one transaction. Returns the persisted released version.
    // When `createParentId` is given (a brand-new schematic's first release), the parent
    // Schematic record is created in the same transaction — its schematicNumber is computed
    // here, at save time, from the freshest parent list (one past the current highest, min 1),
    // so two users creating schematics concurrently don't both grab the same number.
    public releaseSchematicVersion(
        version: SchematicVersion,
        createParentId?: string,
        saved?: SchematicVersion | null,
    ): Observable<SchematicVersion> {
        return forkJoin({
            // Previous drawings can be very large; release only needs their identity and status.
            versions: this.appwrite.listAll<Pick<SchematicVersion, 'id' | 'status'>>(
                Collection.schematicVersion,
                [Query.equal('schematicId', version.schematicId), Query.select(['$id', 'status'])],
            ),
            parents: createParentId ? this.loadSchematicParents() : of([] as Schematic[]),
        }).pipe(
            switchMap(({ versions, parents }) => {
                const validStatuses = [SchematicVersionStatus.Released, SchematicVersionStatus.Obsolete];
                const nowObsolete = versions
                    .filter(v => v.id !== version.id && validStatuses.includes(v.status));
                const released: SchematicVersion = {
                    ...version,
                    status: SchematicVersionStatus.Released,
                    versionNumber: nowObsolete.length + 1,
                };
                const parent: Schematic | null = createParentId
                    ? {
                        id: createParentId,
                        schematicNumber: parents.reduce((max, p) => Math.max(max, p.schematicNumber ?? 0), 0) + 1,
                    }
                    : null;
                // Already-obsolete versions need no write. Status-only updates leave their
                // nodes/wires intact and keep release atomic with the current version.
                const statusUpdates = nowObsolete
                    .filter(v => v.status === SchematicVersionStatus.Released)
                    .map(v => ({ id: v.id, status: SchematicVersionStatus.Obsolete }));
                const { nodes, wires, ...metadata } = released;
                const releaseUpdate = {
                    ...metadata,
                    ...(nodes !== saved?.nodes ? { nodes } : {}),
                    ...(wires !== saved?.wires ? { wires } : {}),
                };
                return this.appwrite.batchCreateUpdateAndIncrementAcrossCollectionsTransactional(
                    parent ? { [Collection.schematic]: [parent], [Collection.schematicVersion]: [released] } : {},
                    { [Collection.schematicVersion]: [...statusUpdates, ...(parent ? [] : [releaseUpdate])] },
                    [],
                ).pipe(
                    map(() => released),
                );
            }),
        );
    }

    // --- Automation Hardware Items (UX: "components") ---

    public loadAutomationHardwareItems(): Observable<AutomationHardwareItem[]> {
        return this.appwrite.collection<AutomationHardwareItem>(Collection.automationHardwareItem, ref =>
            ref.orderBy('partNumber')
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadAutomationHardwareItemById(id: string): Observable<AutomationHardwareItem> {
        return this.appwrite.collection<AutomationHardwareItem>(Collection.automationHardwareItem).doc(id).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public loadAutomationHardwareItemsByFootprintId(footprintId: string): Observable<AutomationHardwareItem[]> {
        return this.appwrite.collection<AutomationHardwareItem>(Collection.automationHardwareItem, ref =>
            ref.where('footprintId', '==', footprintId)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public setAutomationHardwareItem(item: AutomationHardwareItem): Observable<void> {
        return from(this.appwrite.collection(Collection.automationHardwareItem).doc(item.id).set(item));
    }

    public deleteAutomationHardwareItem(id: string): Observable<void> {
        return from(this.appwrite.collection(Collection.automationHardwareItem).doc(id).delete());
    }

    // --- Engineering Projects ---

    public loadEngineeringProjectEngineers(): Observable<EngineeringProjectEngineer[]> {
        return this.appwrite.collection<EngineeringProjectEngineer>(Collection.engineeringProjectEngineer).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public setEngineeringProjectEngineer(engineer: EngineeringProjectEngineer): Observable<void> {
        return from(this.appwrite.collection(Collection.engineeringProjectEngineer).doc(engineer.id).set(engineer));
    }

    public deleteEngineeringProjectEngineer(id: string): Observable<void> {
        return from(this.appwrite.collection(Collection.engineeringProjectEngineer).doc(id).delete());
    }

    public loadEngineeringProjects(): Observable<EngineeringProject[]> {
        return this.appwrite.collection<EngineeringProject>(Collection.engineeringProject).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public setEngineeringProject(project: EngineeringProject): Observable<void> {
        return from(this.appwrite.collection(Collection.engineeringProject).doc(project.id).set(project));
    }

    public deleteEngineeringProject(id: string): Observable<void> {
        return from(this.appwrite.collection(Collection.engineeringProject).doc(id).delete());
    }

    // --- Automation Vendor Hardware Data ---

    public loadAutomationVendorHardwareData(): Observable<AutomationVendorHardwareData[]> {
        return this.appwrite.collection<AutomationVendorHardwareData>(Collection.automationVendorHardwareData).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadAutomationVendorHardwareDataByHardwareItemId(hardwareItemId: string): Observable<AutomationVendorHardwareData[]> {
        return this.appwrite.collection<AutomationVendorHardwareData>(Collection.automationVendorHardwareData, ref =>
            ref.where('parentHardwareItemId', '==', hardwareItemId)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public setAutomationVendorHardwareData(vendorHardwareData: AutomationVendorHardwareData): Observable<void> {
        return from(this.appwrite.collection(Collection.automationVendorHardwareData).doc(vendorHardwareData.id).set(vendorHardwareData));
    }

    public deleteAutomationVendorHardwareData(id: string): Observable<void> {
        return from(this.appwrite.collection(Collection.automationVendorHardwareData).doc(id).delete());
    }

    // --- Automation Hardware Item Footprints (versioned) ---

    public loadAutomationFootprints(): Observable<AutomationFootprint[]> {
        return this.appwrite.collection<AutomationFootprint>(Collection.automationFootprint).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadAutomationFootprintById(id: string): Observable<AutomationFootprint> {
        return this.appwrite.collection<AutomationFootprint>(Collection.automationFootprint).doc(id).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public loadAutomationFootprintVersions(): Observable<AutomationFootprintVersion[]> {
        return this.appwrite.collection<AutomationFootprintVersion>(Collection.automationFootprintVersion).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    // Keep obsolete versions referenced by placed nodes, plus the released library. Unused
    // historical footprint geometry is unnecessary on the schematic editing page.
    public loadSchematicFootprintVersions(versionIds$: Observable<string[]>): Observable<AutomationFootprintVersion[]> {
        return forkJoin({
            // Read a compact catalogue while the drawing loads, then fetch only the needed
            // geometry by ID. This works with the existing indexes; no status index is required.
            catalogue: this.appwrite.listAll<Pick<AutomationFootprintVersion, 'id' | 'status'>>(Collection.automationFootprintVersion, [
                Query.select(['$id', 'status']),
            ]),
            placedIds: versionIds$,
        }).pipe(
            switchMap(({ catalogue, placedIds }) => this.appwrite.listByIds<AutomationFootprintVersion>(
                Collection.automationFootprintVersion,
                [...new Set([
                    ...placedIds,
                    ...catalogue.filter(v => v.status === AutomationFootprintVersionStatus.Released).map(v => v.id),
                ])],
            )),
        );
    }

    public loadAutomationFootprintVersionsByFootprintId(footprintId: string): Observable<AutomationFootprintVersion[]> {
        return this.appwrite.collection<AutomationFootprintVersion>(Collection.automationFootprintVersion, ref =>
            ref.where('footprintId', '==', footprintId)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadAutomationFootprintVersionById(id: string): Observable<AutomationFootprintVersion> {
        return this.appwrite.collection<AutomationFootprintVersion>(Collection.automationFootprintVersion).doc(id).get().pipe(
            map(fromAppwriteDoc)
        );
    }

    public setAutomationFootprint(footprint: AutomationFootprint): Observable<void> {
        return from(this.appwrite.collection(Collection.automationFootprint).doc(footprint.id).set(footprint));
    }

    public setAutomationFootprintVersion(version: AutomationFootprintVersion): Observable<void> {
        return from(this.appwrite.collection(Collection.automationFootprintVersion).doc(version.id).set(version));
    }

    // Writes the (id-only) parent footprint, then its first version. Used when a
    // brand-new footprint is created from the symbol builder.
    public createAutomationFootprintWithVersion(
        footprint: AutomationFootprint,
        version: AutomationFootprintVersion,
    ): Observable<void> {
        return this.setAutomationFootprint(footprint).pipe(
            switchMap(() => this.setAutomationFootprintVersion(version)),
        );
    }

    public deleteAutomationFootprintVersion(id: string): Observable<void> {
        return from(this.appwrite.collection(Collection.automationFootprintVersion).doc(id).delete());
    }

    // Writes several footprint versions in one transaction. Used on release to mark
    // prior released versions Obsolete and the new one Released atomically.
    public setAutomationFootprintVersionsTransactional(versions: AutomationFootprintVersion[]): Observable<void> {
        return this.appwrite.batchSetAcrossCollectionsTransactional({
            [Collection.automationFootprintVersion]: versions,
        });
    }

    // Releases a version: marks prior released/obsolete versions of the same footprint
    // Obsolete, assigns the next version number, and writes everything in one
    // transaction. The incoming version is expected to already carry its release
    // stamps (releasedById/On + comment). Returns the persisted released version.
    public releaseAutomationFootprintVersion(version: AutomationFootprintVersion): Observable<AutomationFootprintVersion> {
        return this.loadAutomationFootprintVersionsByFootprintId(version.footprintId).pipe(
            switchMap(versions => {
                const validStatuses = [AutomationFootprintVersionStatus.Released, AutomationFootprintVersionStatus.Obsolete];
                const nowObsolete = versions
                    .filter(v => v.id !== version.id && validStatuses.includes(v.status))
                    .map(v => ({ ...v, status: AutomationFootprintVersionStatus.Obsolete }));
                const released: AutomationFootprintVersion = {
                    ...version,
                    status: AutomationFootprintVersionStatus.Released,
                    versionNumber: nowObsolete.length + 1,
                };
                return this.setAutomationFootprintVersionsTransactional([...nowObsolete, released]).pipe(
                    map(() => released),
                );
            }),
        );
    }
}
